import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import invoiceStyles from "../../invoices/styles/invoice.module.css";
import {
  buildChildrenOf, buildHidden, toggle,
  FiltrosBarra, CardsResumen, TableHeader, FilaCargando, FilaVacia, FilaArbol,
} from "./BalanceComprobacion";
import { usePDFIndividual } from "./EstadoFinanciero";
import { useCurrency } from "../../../../shared/contexts/CurrencyContext";

const signColor = (n) => {
  const v = Number(n ?? 0);
  if (Math.abs(v) < 0.005) return "var(--text-muted)";
  return v < 0 ? "#f87171" : "#4ade80";
};

const TIPO_COLORS = {
  ACTIVO:     "#60a5fa",
  PASIVO:     "#f87171",
  PATRIMONIO: "#c084fc",
};

function colorFila(fila) {
  const c = TIPO_COLORS[fila.tipo] ?? "var(--text-primary)";
  if (fila.nivel === 1) return c;
  if (fila.nivel === 2) return "var(--text-primary)";
  return fila.nivel === 3 ? "var(--text-secondary)" : "var(--text-muted)";
}

function bgFila(fila) {
  const TIPO_COLORS_BG = { ACTIVO: "#60a5fa", PASIVO: "#f87171", PATRIMONIO: "#c084fc" };
  if (fila.nivel >= 3) return "transparent";
  const c = TIPO_COLORS_BG[fila.tipo] ?? "var(--border-card)";
  return c + (fila.nivel === 1 ? "12" : "07");
}

function Separador({ label, color }) {
  return (
    <tr>
      <td colSpan={3} style={{ padding: "7px 14px",
        background: color + "22",
        borderLeft: `3px solid ${color}`,
        color, fontSize: "0.78rem", fontWeight: 700,
        textTransform: "uppercase", letterSpacing: "0.8px" }}>
        {label}
      </td>
    </tr>
  );
}

function FilaTotal({ label, monto, color, big }) {
  const { fmtCRCSigned } = useCurrency();
  const montoNum = Number(monto ?? 0);
  const montoColor = montoNum < -0.005 ? "#f87171" : color;
  return (
    <tr style={{ borderTop: `1px solid ${color}40` }}>
      <td colSpan={2} style={{ padding: big ? "10px 12px" : "7px 12px",
        paddingLeft: 26, fontWeight: 700, color, fontSize: big ? "0.88rem" : "0.83rem" }}>
        {label}
      </td>
      <td className="text-end" style={{ padding: big ? "10px 14px" : "7px 14px",
        fontFamily: "monospace", fontWeight: 800, color: montoColor,
        fontSize: big ? "0.92rem" : "0.85rem" }}>
        {fmtCRCSigned(monto)}
      </td>
    </tr>
  );
}

export default function BalanceGeneral() {
  const { t } = useTranslation();
  const { fmtCRCSigned, isUSD, setMultimoneda } = useCurrency();

  // valorFila: uses USD field when toggle is active
  function valorFila(fila) {
    const s = Number(isUSD ? (fila.saldo_usd ?? 0) : (fila.saldo ?? 0));
    if (Math.abs(s) < 0.01) return { text: "—", color: "var(--text-muted)" };
    const typeColor = TIPO_COLORS[fila.tipo] ?? "var(--text-primary)";
    const color = s < -0.005 ? "#f87171" : (fila.nivel <= 2 ? typeColor : "var(--text-secondary)");
    return { text: fmtCRCSigned(s), color };
  }

  const today = new Date().toISOString().split("T")[0];

  const [fechaHasta, setFechaHasta] = useState(today);
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [soloConMov, setSoloConMov] = useState(true);
  const [collapsed,  setCollapsed]  = useState(new Set());
  const pdfBG = usePDFIndividual("/api/finance/reportes/balance-general-pdf/", "situacion_financiera.pdf");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (fechaHasta) qs.set("fecha_hasta", fechaHasta);
      const res = await apiFetch(`/api/finance/reportes/balance-general/?${qs}`);
      const d   = await res.json();
      setData(d);
      setMultimoneda(d.multimoneda_activa ?? false);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [fechaHasta, setMultimoneda]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filas      = data?.filas ?? [];
  const childrenOf = buildChildrenOf(filas);
  const hidden     = buildHidden(filas, childrenOf, collapsed);

  const tieneMovArbol = (id) => {
    const f = filas.find(x => x.id === id);
    if (!f) return false;
    if (f.tiene_movimientos) return true;
    return (childrenOf[id] ?? []).some(tieneMovArbol);
  };

  const byTipo = (tipo) => filas.filter(f =>
    f.tipo === tipo && !hidden.has(f.id) && (!soloConMov || tieneMovArbol(f.id))
  );

  const activos    = byTipo("ACTIVO");
  const pasivos    = byTipo("PASIVO");
  const patrimonio = byTipo("PATRIMONIO");
  const total      = activos.length + pasivos.length + patrimonio.length;

  // Use USD totals when toggle is active — amounts come directly from backend
  const tA   = Number(isUSD ? (data?.total_activo_usd             ?? 0) : (data?.total_activo            ?? 0));
  const tP   = Number(isUSD ? (data?.total_pasivo_usd             ?? 0) : (data?.total_pasivo            ?? 0));
  const tPat = Number(isUSD ? (data?.total_patrimonio_usd         ?? 0) : (data?.total_patrimonio        ?? 0));
  const tPP  = Number(isUSD ? (data?.total_pasivo_patrimonio_usd  ?? 0) : (data?.total_pasivo_patrimonio ?? 0));
  const bal  = data?.balanceado ?? true;

  return (
    <>
      <span className={invoiceStyles.inactive}>
        <Link to="/">
          <span className={invoiceStyles.inactive}>{t("common.inicio")} /</span>
        </Link>
        <span className={invoiceStyles.active}> {t("reportes.balanceGeneral")}</span>
      </span>
      <div>
        <h2 className="title mt-2 mb-2">{t("reportes.balanceGeneral")}</h2>
        <p className="mb-3" style={{ color: "var(--text-muted)" }}>{t("reportes.activosPasivosDesc")}</p>
      </div>

      <FiltrosBarra
        fechaHasta={fechaHasta} setFechaHasta={setFechaHasta}
        soloConMov={soloConMov} setSoloConMov={setSoloConMov}
        onActualizar={fetchData} loading={loading}
        extraButtons={
          <>
            <button onClick={() => pdfBG.generar(`fecha_hasta=${fechaHasta}`)}
              disabled={pdfBG.loading || loading || !data}
              style={{ padding: "7px 14px", background: "rgba(59,130,246,0.15)",
                border: "1px solid rgba(59,130,246,0.5)", borderRadius: 6,
                color: "#60a5fa", fontWeight: 600, cursor: "pointer", fontSize: "0.85rem",
                opacity: (pdfBG.loading || !data) ? 0.5 : 1 }}>
              {pdfBG.loading
                ? <><i className="fa-solid fa-circle-notch fa-spin"/> {t("reportes.generando")}</>
                : <><i className="fa-solid fa-file-pdf" style={{ marginRight: 5 }}/>{t("reportes.pdf")}</>}
            </button>
            {pdfBG.modal}
            {pdfBG.error && (
              <div style={{ padding: "6px 12px", background: "rgba(239,68,68,0.1)",
                border: "1px solid rgba(239,68,68,0.3)", borderRadius: 6,
                color: "#f87171", fontSize: "0.8rem" }}>
                <i className="fa-solid fa-circle-exclamation" style={{ marginRight: 5 }}/>
                {pdfBG.error}
              </div>
            )}
          </>
        }
      />

      {data && (
        <CardsResumen cards={[
          { label: t("reportes.totalActivos"),    value: Math.abs(tA),   prefix: tA   < 0 ? "-" : "", color: tA   < 0 ? "#f87171" : "#60a5fa", icon: "fa-building" },
          { label: t("reportes.totalPasivos"),    value: Math.abs(tP),   prefix: tP   < 0 ? "-" : "", color: tP   < 0 ? "#f87171" : "#f87171", icon: "fa-file-invoice" },
          { label: t("reportes.totalPatrimonio"), value: Math.abs(tPat), prefix: tPat < 0 ? "-" : "", color: tPat < 0 ? "#f87171" : "#c084fc", icon: "fa-landmark" },
          { label: bal ? t("reportes.balanceadoOk") : t("reportes.diferenciaWarn"),
            value: Math.abs(Number(data.diferencia)),
            color: bal ? "#4ade80" : "#fbbf24",
            icon:  bal ? "fa-scale-balanced" : "fa-triangle-exclamation" },
        ]}/>
      )}

      <div className={invoiceStyles["table-container"]}>
        <TableHeader
          count={total} loading={loading}
          onColapsar={() => setCollapsed(new Set(filas.filter(f => f.nivel < 4).map(f => f.id)))}
          onExpandir={() => setCollapsed(new Set())}
        />
        <div className={`${invoiceStyles["table-body"]} ${invoiceStyles["table-responsive"]}`}>
          <table>
            <thead>
              <tr>
                <th style={{ width: 130 }}>{t("reportes.codigo")}</th>
                <th>{t("reportes.nombre")}</th>
                <th className="text-end" style={{ width: 160 }}>{t("reportes.saldo")}</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? <FilaCargando colSpan={3} msg={t("reportes.calculandoBalanceGeneral")}/>
                : total === 0
                  ? <FilaVacia colSpan={3} soloConMov={soloConMov} onMostrarTodas={() => setSoloConMov(false)}/>
                  : <>
                    {activos.length > 0 && <>
                      <Separador label={t("reportes.activos")} color="#60a5fa"/>
                      {activos.map(f => <FilaArbol key={f.id} fila={f} collapsed={collapsed}
                        onToggle={id => setCollapsed(prev => toggle(prev, id))}
                        colorFn={colorFila} valorFn={valorFila} bgFn={bgFila}/>)}
                      <FilaTotal label={t("reportes.totalActivos")} monto={tA} color="#60a5fa"/>
                    </>}
                    {pasivos.length > 0 && <>
                      <Separador label={t("reportes.pasivos")} color="#f87171"/>
                      {pasivos.map(f => <FilaArbol key={f.id} fila={f} collapsed={collapsed}
                        onToggle={id => setCollapsed(prev => toggle(prev, id))}
                        colorFn={colorFila} valorFn={valorFila} bgFn={bgFila}/>)}
                      <FilaTotal label={t("reportes.totalPasivos")} monto={tP} color="#f87171"/>
                    </>}
                    {patrimonio.length > 0 && <>
                      <Separador label={t("reportes.patrimonio")} color="#c084fc"/>
                      {patrimonio.map(f => <FilaArbol key={f.id} fila={f} collapsed={collapsed}
                        onToggle={id => setCollapsed(prev => toggle(prev, id))}
                        colorFn={colorFila} valorFn={valorFila} bgFn={bgFila}/>)}
                      <FilaTotal label={t("reportes.totalPatrimonio")} monto={tPat} color="#c084fc"/>
                    </>}

                    {data && (
                      <>
                        <FilaTotal label={t("reportes.totalPasivoPatrimonio")} monto={tPP} color="var(--text-secondary)" big/>
                        <tr style={{ background: bal
                            ? "rgba(249,115,22,0.08)" : "rgba(251,191,36,0.08)",
                          borderTop: `3px solid ${bal ? "#f97316" : "#fbbf24"}` }}>
                          <td colSpan={2} style={{ padding: "12px 12px", fontWeight: 800,
                            fontSize: "0.92rem", color: bal ? "#f97316" : "#fbbf24" }}>
                            {bal ? t("reportes.activosEquals") : t("reportes.diferenciaPasivos")}
                          </td>
                          <td className="text-end" style={{ padding: "12px 14px",
                            fontFamily: "monospace", fontWeight: 800, fontSize: "1rem",
                            color: bal ? "#4ade80" : "#fbbf24" }}>
                            {bal ? t("reportes.balanceadoCheck") : fmtCRCSigned(isUSD ? (tA - tPP) : Number(data.diferencia ?? 0))}
                          </td>
                        </tr>
                      </>
                    )}
                  </>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
